/* -----------------Globals--------------- */
import {
	Box,
	DataCard,
	ProgressSummaryCard,
	StudySessionMasteryTagProgressCard,
} from "@memorang/ui";

import type { MasteryChartCardProps } from "./MasteryChartCard";
/* -----------------Child components--------------- */
import ProgressCardsContainer from "./ProgressCardsContainer";

import { SessionConfigDialog } from "@features/session-config";
/* -----------------Types--------------- */
import type { MasteryTagProgressData } from "@helpers/content/types";
import type { ExamCardData, SummaryCardData } from "@helpers/progress-data";
import { smartPluralize } from "@helpers/smartPluralize";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	getBarChartDataFromTags,
	getCircularGaugeGridDataFromTags,
	getRadarChartDataFromTags,
} from "./dataCardChartDataFromTags";
/* -----------------Dummy data (screenshot / write-in-progress)--------------- */
import {
	dummyCircularChartData,
	dummyComparisonChartData,
	dummyLineChartData,
} from "./dummyProgressDataCards";

interface Props {
	masteryTagsProgressData: MasteryTagProgressData[];
	itemTypeProgressData: MasteryTagProgressData | null;
	masteryChartProgressData?: MasteryChartCardProps | null;
	needsImprovementProgressData: MasteryTagProgressData | null;
	contentIds?: string[];
	summaryCardData?: SummaryCardData;
	practiceTestsCardData?: ExamCardData;
	showStudyPackViews?: boolean;
	numItemsAttempted?: number;
}
const ProgressView = ({
	masteryTagsProgressData,
	needsImprovementProgressData,
	summaryCardData,
	showStudyPackViews,
	numItemsAttempted = 0,
}: Props) => {
	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);

	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const hideAnalyticsCards = demoExamId === "proethica";

	const { t } = useTranslation();
	const { handleSessionConfigNavigation } = useCustomNavigation();
	const weakTagIds = needsImprovementProgressData
		? needsImprovementProgressData.items.map((item) => item.id!)
		: [];
	const toggleSessionConfigDialog = () => {
		setShowSessionConfigDialog(!showSessionConfigDialog);
	};

	const handleClickStudy = () => {
		const tagIds = needsImprovementProgressData
			? needsImprovementProgressData.items.map((item) => item.id!)
			: [];
		handleSessionConfigNavigation(toggleSessionConfigDialog, {
			selectedTagIds: tagIds,
			reset: "true",
		});
	};

	const masteryTagsCards = masteryTagsProgressData.map((item) => {
		return (
			//@ts-ignore
			<StudySessionMasteryTagProgressCard
				// handleClickCard={() => handleClickCard(item.title)}
				key={item.title}
				{...item}
			/>
		);
	});

	// Same tag data as StudySessionMasteryTagProgressCard (masteryTagsProgressData.items)
	const radarChartData = getRadarChartDataFromTags(masteryTagsProgressData);
	const barChartData = getBarChartDataFromTags(masteryTagsProgressData);
	const circularGaugeGridData = getCircularGaugeGridDataFromTags(
		masteryTagsProgressData,
	);

	// Match old cards: tag-based cards use progress.tagProgress.subtitle; summary-style use progress.summary.subtitle
	const skillCount = masteryTagsProgressData.reduce(
		(sum, section) => sum + section.items.length,
		0,
	);
	const tagBasedSubtitle = t("progress.tagProgress.subtitle", {
		numItems: numItemsAttempted,
		type: smartPluralize("skill", skillCount),
		count: skillCount,
		item: smartPluralize("item", numItemsAttempted),
	});
	const summaryStyleSubtitle = t("progress.summary.subtitle", {
		count: numItemsAttempted,
		item: smartPluralize("item", numItemsAttempted),
	});

	const dataCards = [
		<DataCard
			key="data-card-comparison"
			config={{
				chartType: "comparison",
				headerIconName: "chart-line",
				headerTitle: "Performance comparison",
				headerSubtitle: summaryStyleSubtitle,
				showLegend: true,
			}}
			data={{
				chartData: dummyComparisonChartData,
				legendData: [
					{ label: "You", color: "#4f4a43" },
					{ label: "Top performers", color: "#7a5f00" },
				],
			}}
		/>,
		<DataCard
			key="data-card-line"
			config={{
				chartType: "line",
				headerIconName: "chart-timeline-variant",
				headerTitle: "Test readiness",
				headerSubtitle: summaryStyleSubtitle,
				showLegend: true,
			}}
			data={{
				chartData: dummyLineChartData,
				legendData: [
					{ label: "Actual", color: "#7a5f00" },
					{ label: "Projected", color: "#4b6cb7" },
					{ label: "Target", color: "#66bb68" },
				],
			}}
		/>,
		<DataCard
			key="data-card-bar"
			config={{
				chartType: "bar",
				headerIconName: "chart-bar",
				headerTitle: "Study time by skill",
				headerSubtitle: tagBasedSubtitle,
				tooltip:
					"This chart shows total time invested per skill. Bars above the recommended minimum indicate healthy practice time.",
			}}
			data={{
				chartData: barChartData,
				threshold: 45,
			}}
		/>,
		<DataCard
			key="data-card-radar"
			config={{
				chartType: "radar",
				headerIconName: "radar",
				headerTitle: "Skill performance",
				headerSubtitle: tagBasedSubtitle,
				showLegend: true,
			}}
			data={{
				chartData: radarChartData,
				legendData: [
					{ label: "You", color: "#7a5f00" },
					{ label: "Target", color: "#1f9d57" },
					{ label: "Peer average", color: "#4b6cb7" },
				],
			}}
		/>,
		<DataCard
			key="data-card-circular"
			config={{
				chartType: "circular",
				headerIconName: "fire",
				headerTitle: "Study streak",
				headerSubtitle: summaryStyleSubtitle,
			}}
			data={{
				chartData: dummyCircularChartData,
			}}
		/>,
		<DataCard
			key="data-card-gauge-grid"
			config={{
				chartType: "circularGaugeGrid",
				headerIconName: "gauge",
				headerTitle: "Confidence by skill",
				headerSubtitle: tagBasedSubtitle,
			}}
			data={{
				chartData: circularGaugeGridData,
			}}
		/>,
	];

	const cards = [
		...(summaryCardData
			? [<ProgressSummaryCard {...summaryCardData} key="summary-card" />]
			: []),
		<Box gap={16} key="mastery-tags-cards">
			{masteryTagsCards}
		</Box>,
		...(needsImprovementProgressData
			? [
					//@ts-ignore
					<StudySessionMasteryTagProgressCard
						handleClickCard={showStudyPackViews ? handleClickStudy : undefined}
						{...needsImprovementProgressData}
						cta={
							showStudyPackViews
								? t("progress.needsImprovement.cta")
								: undefined
						}
						key="needs-improvement-card"
					/>,
				]
			: []),
		...(hideAnalyticsCards ? [] : dataCards),
	];
	return (
		<Box gap={16}>
			<ProgressCardsContainer cards={cards} />
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					selectedTagIds={weakTagIds}
					handleClose={toggleSessionConfigDialog}
				/>
			) : null}
		</Box>
	);
};

export default ProgressView;
